import React from 'react'
import { PaymentIcons } from '~/components/Icons'
import { useCountry, useTranslations } from '~/components/Locale'
import { Progress } from '~/components/ProgressBar'
import { Price } from '~/components/utils'
import { Upsell } from '~/pages/ProductPage/Upsells'
import { cartCalcSubTotalDiscount, cn, getPrice, useMarket, useSelector, useShippingCost } from '~/utils'
import './Cart.less'
import { CartItem } from './CartItem'
import { DiscountIcon } from './DiscountIcon'

export const Cart: React.FC = () => {
	const cart = useSelector(s => s.cart)
	const country = useCountry()
	const t = useTranslations('react')
	const shipping = useShippingCost(cart)
	const market = useMarket()

	if (!cart.loaded || !shipping) return null

	const cartDisabled = cart.lines.reduce((a, b) => b.quantity + a, 0) < 1
	const requiresShipping = cart.lines.some(i => i.merchandise?.requiresShipping)

	const discount = cartCalcSubTotalDiscount(cart)
	const subtotal = getPrice(cart.cost.subtotalAmount)

	/** Total price is guesstimated while cart is waiting for a request to complete as there may be unknown discounts */
	const total = getPrice(cart.cost.totalAmount) + shipping.cost
	const totalDiscount = discount + shipping.saved
	const showBanner =
		cart.lines.length == 0 || discount > 0 || (discount === 0 && shipping.cost === 0 && requiresShipping)
	const shippingCountry = country.name ?? cart?.buyerIdentity.countryCode

	return (
		<div className="cart">
			<div className="px-12 py-6">
				<p>{t?.checkout.your_cart}</p>
			</div>

			<div className="cart__content">
				{showBanner && (
					<div className="cart__banner h-18 flex flex-grow items-center justify-center p-0">
						{cart.lines.length == 0 && <p className="text-center">{t?.checkout.cart_empty}.</p>}
						{discount > 0 ? (
							<>
								<span className="text-green-600">{t?.checkout.discount_added}</span>
								<DiscountIcon height="36" className="ml-4" />
							</>
						) : requiresShipping && shipping.cost === 0 ? (
							<>
								<span className="text-green-600">{t?.checkout.free_shipping_added}</span>
								<DiscountIcon height="36" className="ml-4" />
							</>
						) : null}
					</div>
				)}
				<ul className="cart__list px-8">
					{cart.lines.map(lineItem => (
						<CartItem key={lineItem.id} lineItem={lineItem} />
					))}
				</ul>
				<ul className="list mt-4 space-y-4 px-8 pb-4">
					{cart.upsells
						.filter(u => u.item.availableForSale)
						.filter(u => !cart.lines.some(li => li.merchandise?.id === u.item.id))
						.filter(u => !cart.lines.some(li => li.merchandise?.product.id === u.item.product.id))
						.map(upsell => (
							<Upsell
								className="!border-t-transparent"
								key={upsell.item.id}
								variant={upsell.item}
								label={upsell.offer}
								expiration={upsell.expiry}
								expirationText={upsell.expiryText}
								mode="cart"
							/>
						))}
				</ul>
			</div>
			{cart.errors && (
				<div className="cart__error cursor-pointer" onClick={_ => location.reload()}>
					{cart.errors[0].message}. {t?.actions.click_here_to_reload}.
				</div>
			)}
			{cart.lines.length > 0 && (
				<div className="cart__footer bg-gray-0 px-12 py-6">
					<table>
						<tbody>
							<tr>
								<td>{t?.checkout.subtotal}</td>
								<td>
									<Price original={subtotal + discount} price={subtotal} asText decimals={0} />
								</td>
							</tr>
							{requiresShipping && (
								<tr>
									<td>
										{shippingCountry && market?.currency === 'EUR' && window.Shopify.locale === 'en'
											? t?.checkout.shipping_to?.replace('{COUNTRY}', shippingCountry)
											: t?.checkout.shipping}
									</td>
									<td>
										{shipping.cost != 0 && (
											<div className="green mr-1 inline-block align-text-bottom text-base leading-tight">
												<div>{`${t?.checkout.free_shipping_in} ${getPrice(
													shipping.threshold - subtotal,
													true
												)}`}</div>
												<Progress current={subtotal} max={shipping.threshold} />
											</div>
										)}
										<Price
											original={getPrice(shipping.minCost, true, 0)}
											price={shipping.cost > 0 ? getPrice(shipping.cost, true) : t?.checkout.free}
										/>
									</td>
								</tr>
							)}
							{t?.checkout.taxes_and_duties && (
								<tr>
									<td>
										{market?.currency === 'USD'
											? t?.checkout.satisfaction_guarantee
											: t?.checkout.taxes_and_duties}
									</td>
									<td className="green">{t.checkout.included}</td>
								</tr>
							)}
						</tbody>
					</table>
					<hr />
					<div className="text-e2 flex items-center justify-between space-x-2">
						<div>{t?.checkout.total}</div>
						{totalDiscount > 0 && (
							<span className="flex-auto whitespace-nowrap leading-none">
								<div className="cart__savings inline-block px-5 py-2.5 text-base font-bold">
									<span>{t?.checkout.you_save} </span>
									<Price price={totalDiscount} asText decimals={0} />
								</div>
							</span>
						)}
						<Price price={total} asText decimals={0} />
					</div>
					<a
						aria-disabled={cartDisabled}
						href={cartDisabled ? undefined : cart.checkoutUrl}
						target="_self"
						className={cn('btn', 'cart__checkout', { disabled: cartDisabled })}
					>
						<span>{t?.checkout.go_to_checkout}</span>
					</a>
					{window.ShopifyHeader.settings.show_payment_icons && (
						<>
							<PaymentIcons size="small" className="xs:hidden justify-center" />
							<PaymentIcons size="normal" className="xs:flex hidden justify-center" />
						</>
					)}
				</div>
			)}
		</div>
	)
}
